import type { EventingContext } from '../types/context';
import type {
  NormalizedInteraction,
  NormalizedView,
} from '../types/normalized';
import type {
  EventingAdapter,
  NormalizedConversion,
  NormalizedPageView,
} from './types';

type AdapterMethod =
  | 'handleInteraction'
  | 'handleView'
  | 'handlePageView'
  | 'handleConversion';

export interface FanOutAdapterErrorDetails {
  readonly adapterName: string;
  readonly method: AdapterMethod;
}

export interface FanOutAdapterOptions {
  readonly onError?: (
    error: unknown,
    details: FanOutAdapterErrorDetails,
  ) => void;
}

export class FanOutAdapter implements EventingAdapter {
  readonly name = 'fan-out';

  private readonly adapters: readonly EventingAdapter[];
  private readonly onError?: FanOutAdapterOptions['onError'];

  constructor(
    adapters: readonly EventingAdapter[],
    options: FanOutAdapterOptions = {},
  ) {
    this.adapters = adapters;
    this.onError = options.onError;
  }

  handleInteraction(
    interaction: NormalizedInteraction,
    context: EventingContext,
  ): void {
    this.dispatch('handleInteraction', interaction, context);
  }

  handleView(view: NormalizedView, context: EventingContext): void {
    this.dispatch('handleView', view, context);
  }

  handlePageView(pageView: NormalizedPageView, context: EventingContext): void {
    this.dispatch('handlePageView', pageView, context);
  }

  handleConversion(
    conversion: NormalizedConversion,
    context: EventingContext,
  ): void {
    this.dispatch('handleConversion', conversion, context);
  }

  private dispatch(
    method: AdapterMethod,
    event:
      | NormalizedInteraction
      | NormalizedView
      | NormalizedPageView
      | NormalizedConversion,
    context: EventingContext,
  ): void {
    for (const adapter of this.adapters) {
      try {
        const handler = adapter[method] as
          | ((value: typeof event, eventContext: EventingContext) => void)
          | undefined;
        handler?.call(adapter, event, context);
      } catch (error) {
        this.onError?.(error, { adapterName: adapter.name, method });
      }
    }
  }
}
